import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import NewAccount from "./NewAccount";

const blue = "#0A3D91";
const blueLight = "#0D6EFD";
const borderSoft = "rgba(10,61,145,0.12)";

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
};

const Register = () => {
  const [identificacion, setIdentificacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [csrfReady, setCsrfReady] = useState(false);

  useEffect(() => {
    const cargarCsrf = async () => {
      try {
        await fetch("/clientes/public/csrf/", {
          method: "GET",
          credentials: "same-origin",
        });
        setCsrfReady(true);
      } catch (err) {
        setError("No se pudo preparar la seguridad del formulario.");
      }
    };

    cargarCsrf();
  }, []);

  const limpiarIdentificacion = (value) => {
    return String(value || "").replace(/\D/g, "").slice(0, 13);
  };

  const consultarCliente = async () => {
    setError("");
    setResultado(null);

    const id = limpiarIdentificacion(identificacion);

    if (!id) {
      setError("Ingrese su RUC o cédula.");
      return;
    }

    if (![10, 13].includes(id.length)) {
      setError("Ingrese una cédula de 10 dígitos o un RUC de 13 dígitos.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/clientes/public/consultar/", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        body: JSON.stringify({
          identificacion: id,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(data.message || "No se pudo consultar la identificación.");
        return;
      }

      setResultado(data);
    } catch (err) {
      setError("No se pudo conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    consultarCliente();
  };

  const reiniciar = () => {
    setResultado(null);
    setError("");
    setIdentificacion("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #ffffff 0%, #f4f8ff 55%, #ffffff 100%)",
        px: 2,
        py: { xs: 5, md: 7 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "980px",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            mb: 4,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: blue,
              fontSize: { xs: "1.8rem", md: "2.45rem" },
              fontWeight: 800,
              lineHeight: 1.12,
              mb: 1.5,
            }}
          >
            Registro de cliente
          </Typography>

          <Typography
            sx={{
              color: "rgba(0,0,0,0.68)",
              fontSize: { xs: "1rem", md: "1.05rem" },
              lineHeight: 1.7,
              maxWidth: "760px",
              mx: "auto",
            }}
          >
            Ingresa tu RUC o cédula para verificar tu registro y activar tu
            cuenta de cliente mediante correo electrónico.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#fff",
            border: `1px solid ${borderSoft}`,
            borderRadius: "26px",
            p: { xs: 2.5, sm: 4, md: 5 },
          }}
        >
          {!resultado && (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 800,
                  color: "#111",
                  mb: 1,
                }}
              >
                RUC o cédula
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems="stretch"
              >
                <TextField
                  fullWidth
                  value={identificacion}
                  onChange={(e) =>
                    setIdentificacion(limpiarIdentificacion(e.target.value))
                  }
                  placeholder="Ingrese su identificación"
                  inputProps={{
                    maxLength: 13,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: borderSoft,
                      },
                      "&:hover fieldset": {
                        borderColor: blue,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: blue,
                      },
                    },
                    "& .MuiInputBase-input": {
                      py: 1.35,
                    },
                  }}
                />

                <Button
                  type="submit"
                  disabled={loading || !csrfReady}
                  variant="contained"
                  sx={{
                    minWidth: { xs: "100%", sm: "180px" },
                    backgroundColor: blue,
                    color: "#fff",
                    px: 3.5,
                    py: 1.25,
                    borderRadius: "14px",
                    fontWeight: 800,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: blueLight,
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(10,61,145,0.35)",
                      color: "#fff",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                  ) : (
                    "CONSULTAR"
                  )}
                </Button>
              </Stack>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 3,
                borderRadius: "14px",
              }}
            >
              {error}
            </Alert>
          )}

          {resultado && (
            <NewAccount
              resultado={resultado}
              identificacion={identificacion}
              onBack={reiniciar}
              getCookie={getCookie}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Register;